import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCompanies = useCallback(async () => {
    try {
      const token = localStorage.getItem("stancor_token");
      if (!token) { setLoading(false); return; }
      const res = await api.get("/companies");
      const list = res.data || [];
      setCompanies(list);

      // Restore saved company or default to first
      const savedId = localStorage.getItem("stancor_company_id");
      const match = list.find(c => c.id === savedId);
      const selected = match || list[0] || null;
      setActiveCompany(selected);
      if (selected) localStorage.setItem("stancor_company_id", selected.id);
    } catch {
      /* ignore — user might not be logged in */
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for storage events (login/logout sets token)
  useEffect(() => {
    fetchCompanies();
    const onStorage = () => fetchCompanies();
    window.addEventListener("storage", onStorage);
    // Also listen for custom event dispatched after login
    window.addEventListener("stancor-auth-change", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("stancor-auth-change", onStorage);
    };
  }, [fetchCompanies]);

  const switchCompany = (companyId) => {
    const c = companies.find(co => co.id === companyId);
    if (c && c.id !== activeCompany?.id) {
      localStorage.setItem("stancor_company_id", c.id);
      // Full reload so branding, permissions and page data all refresh for the new company
      window.location.reload();
    }
  };

  return (
    <CompanyContext.Provider value={{ companies, activeCompany, loading, switchCompany, fetchCompanies }}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used within CompanyProvider");
  return ctx;
};
